import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useCountryStateCity } from '../../hooks/useCountryStateCity';
import API_URL from '../../config';

const AddLead = () => {
  const navigate = useNavigate();
  const { countries, states, cities, getStatesOfCountry, getCitiesOfState } = useCountryStateCity();
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    email: '',
    city: '',
    address: '',
    source: '',
    status: 'New',
    priority: 'Low',
    remarks: '',
  });

  const handleCountryChange = (e) => {
    const countryCode = e.target.value;
    setSelectedCountry(countryCode);
    getStatesOfCountry(countryCode);
  };

  const handleStateChange = (e) => {
    const stateCode = e.target.value;
    setSelectedState(stateCode);
    getCitiesOfState(selectedCountry, stateCode);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const countryName = countries.find(c => c.isoCode === selectedCountry)?.name || selectedCountry;
    const stateName = states.find(s => s.isoCode === selectedState)?.name || selectedState;
    
    const fullData = {
      name: formData.name.trim(),
      phone: formData.phoneNumber.trim(),
      email: formData.email.trim(),
      country: countryName,
      userType: "lead",
      organization: formData.address ? `${formData.address}, ${formData.city}, ${stateName}`.trim() : "",
      interest: formData.source || "Website",
      note: formData.remarks || "",
      status: formData.status,
      priority: formData.priority,
    };

    try {
      setSubmitting(true);
      const response = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(fullData),
      });

      const resData = await response.json();
      if (!response.ok || resData.success === false) {
        throw new Error(resData.message || "Failed to create lead");
      }

      toast.success("Lead created successfully!");
      navigate("/leads");
    } catch (err) {
      setError(err.message || "Failed to save lead");
      toast.error(err.message || "Failed to save lead");
    } finally {
      setSubmitting(false);
    }
  };

  const sourceOptions = [
    "Website",
    "Referral",
    "Social Media",
    "Advertisement",
    "Other"
  ];

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-header border-bottom bg-transparent py-3">
              <h5 className="mb-0 fw-semibold text-dark">Add New Lead</h5>
            </div>
            <div className="card-body p-4">
              {error && (
                <div className="alert alert-danger mb-4" role="alert">
                  {error}
                </div>
              )}
              <form className="row g-3" onSubmit={handleSubmit}>
                <div className="col-md-6">
                  <label className="form-label text-secondary">Name</label>
                  <input type="text" className="form-control" placeholder="Enter full name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>

                <div className="col-md-6">
                  <label className="form-label text-secondary">Phone Number</label>
                  <input type="text" className="form-control" placeholder="Enter phone number" value={formData.phoneNumber} onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })} required />
                </div>

                <div className="col-md-6">
                  <label className="form-label text-secondary">Email</label>
                  <input type="email" className="form-control" placeholder="Enter email address" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
                </div>

                <div className="col-md-6">
                  <label className="form-label text-secondary">Country</label>
                  <select className="form-select" onChange={handleCountryChange} required>
                    <option value="">Select Country</option>
                    {countries.map(country => (
                      <option key={country.isoCode} value={country.isoCode}>{country.name}</option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label text-secondary">State</label>
                  <select className="form-select" onChange={handleStateChange} required>
                    <option value="">Select State</option>
                    {states.map(state => (
                      <option key={state.isoCode} value={state.isoCode}>{state.name}</option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label text-secondary">City</label>
                  <select className="form-select" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} required>
                    <option value="">Select City</option>
                    {cities.map(city => (
                      <option key={city.name} value={city.name}>{city.name}</option>
                    ))}
                  </select>
                </div>

                <div className="col-12">
                  <label className="form-label text-secondary">Address</label>
                  <input type="text" className="form-control" placeholder="Enter full address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} required />
                </div>

                <div className="col-md-4">
                  <label className="form-label text-secondary">Source</label>
                  <input list="source-options" className="form-control" placeholder="Select or type a source" value={formData.source} onChange={(e) => setFormData({ ...formData, source: e.target.value })} required />
                  <datalist id="source-options">
                    {sourceOptions.map(opt => <option key={opt} value={opt} />)}
                  </datalist>
                </div>

                <div className="col-md-4">
                  <label className="form-label text-secondary">Status</label>
                  <select className="form-select" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                    <option>New</option>
                    <option>Contacted</option>
                    <option>Qualified</option>
                    <option>Lost</option>
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label text-secondary">Priority</label>
                  <select className="form-select" value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value })}>
                    <option>Low</option>
                    <option>Medium</option>
                    <option>High</option>
                  </select>
                </div>

                <div className="col-12">
                  <label className="form-label text-secondary">Remarks</label>
                  <textarea className="form-control" rows="3" placeholder="Add any remarks" value={formData.remarks} onChange={(e) => setFormData({ ...formData, remarks: e.target.value })} />
                </div>

                <div className="col-12 d-flex justify-content-end gap-2">
                  <button type="button" className="btn btn-secondary" onClick={() => navigate("/leads")} disabled={submitting}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? "Adding..." : "Add Lead"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddLead;